import { Cat, KINDS } from '../../../gameplay/registry';
import { TICK_RATE } from '../../../gameplay/stats';
import { C } from '../../../gameplay/types';
import { FX_ONE, isqrt } from '../fixed';
import { MAX_PROJECTILES, Order, StanceId, UState, type World } from '../world';
import { approach, setDestination, stopMoving } from './movement';

/** Compute damage dealt by an attacker kind/owner to target slot. Pure integer maths. */
export function computeDamage(w: World, atkOwner: number, atkKind: number, t: number, attackerHeight: number): number {
  const ast = w.players[atkOwner].stats[atkKind];
  const def = KINDS[atkKind].unit?.attack ?? KINDS[atkKind].building?.attack;
  if (!def) return 0;
  const tOwner = w.owner[t];
  const tst = tOwner >= 0 ? w.players[tOwner].stats[w.kind[t]] : null;
  const tClasses = tst ? tst.classes : 0;
  let meleeArmor = tst ? tst.meleeArmor : 0;
  let pierceArmor = tst ? tst.pierceArmor : 0;
  // Formation effects on the defender.
  const tf = w.cat[t] === Cat.Unit ? w.formation[t] : 0;
  if (tf === 2) meleeArmor -= 1; // column: exposed flanks
  if (tf === 3 || tf === 5) meleeArmor += 2; // box / square: braced
  if (tf === 1) pierceArmor += 1; // line: shields forward
  let dmg = Math.max(0, ast.meleeAttack - meleeArmor) + Math.max(0, ast.pierceAttack - pierceArmor);
  let bonus = 0;
  for (const b of def.bonuses ?? []) {
    if (b.vs & tClasses) {
      let bd = b.dmg;
      // Braced formations halve anti-cavalry charge bonus received... and blunt cavalry bonuses.
      if ((tf === 3 || tf === 5) && KINDS[atkKind].unit && KINDS[atkKind].unit!.classes & C.Cavalry) bd = bd >> 1;
      bonus += bd;
    }
  }
  bonus = Math.trunc((bonus * (100 + ast.bonusDmgPct)) / 100);
  dmg += bonus;
  // Elevation: high ground +25%, low ground -25%.
  const th = w.heightAt(w.x[t], w.y[t]);
  if (attackerHeight - th > 24) dmg = Math.trunc((dmg * 125) / 100);
  else if (th - attackerHeight > 24) dmg = Math.trunc((dmg * 75) / 100);
  return Math.max(1, dmg);
}

export function applyDamage(w: World, t: number, dmg: number, attackerOwner: number, splash: boolean): void {
  if (!w.alive[t] || w.cat[t] === Cat.Resource || w.cat[t] === Cat.Held) return;
  w.hp[t] -= dmg;
  const tOwner = w.owner[t];
  w.events.push({ e: 'hit', x: w.x[t], y: w.y[t], kind: w.kind[t], dmg, owner: attackerOwner, targetOwner: tOwner, building: w.cat[t] === Cat.Building, splash });
  if (tOwner >= 0 && w.tick - w.lastHurtTick[t] > TICK_RATE * 8) {
    w.events.push({ e: 'underAttack', owner: tOwner, x: w.x[t], y: w.y[t], building: w.cat[t] === Cat.Building });
  }
  w.lastHurtTick[t] = w.tick;
  if (w.hp[t] <= 0) {
    if (attackerOwner >= 0 && w.cat[t] === Cat.Unit) w.players[attackerOwner].kills++;
    w.kill(t);
  }
}

function spawnProjectile(w: World, s: number, t: number, accuracy: number): void {
  const p = w.projectiles;
  let slot = -1;
  for (let i = 0; i < MAX_PROJECTILES; i++) {
    const j = (p.count + i) % MAX_PROJECTILES;
    if (!p.alive[j]) {
      slot = j;
      break;
    }
  }
  if (slot < 0) return;
  p.count = (slot + 1) % MAX_PROJECTILES;
  const def = KINDS[w.kind[s]].unit?.attack ?? KINDS[w.kind[s]].building!.attack!;
  const hit = w.rng.int(100) < accuracy;
  let tx = w.x[t];
  let ty = w.y[t];
  if (!hit) {
    tx += w.rng.range(-FX_ONE, FX_ONE);
    ty += w.rng.range(-FX_ONE, FX_ONE);
  }
  const dx = tx - w.x[s];
  const dy = ty - w.y[s];
  const dist = isqrt(dx * dx + dy * dy);
  const speed = Math.round(((def.projectileSpeed ?? 10) * FX_ONE) / TICK_RATE);
  p.alive[slot] = 1;
  p.owner[slot] = w.owner[s];
  p.kind[slot] = w.kind[s];
  p.sx[slot] = w.x[s];
  p.sy[slot] = w.y[s];
  p.tx[slot] = tx;
  p.ty[slot] = ty;
  p.target[slot] = hit ? w.idOf(t) : -1;
  p.t[slot] = 0;
  p.total[slot] = Math.max(1, Math.ceil(dist / speed));
  p.hits[slot] = w.heightAt(w.x[s], w.y[s]) >> 2; // attacker height (quantised) for elevation bonus
  w.events.push({ e: 'projectile', slot });
}

function updateProjectiles(w: World): void {
  const p = w.projectiles;
  for (let i = 0; i < MAX_PROJECTILES; i++) {
    if (!p.alive[i]) continue;
    p.t[i]++;
    if (p.t[i] < p.total[i]) continue;
    p.alive[i] = 0;
    const owner = p.owner[i];
    const k = p.kind[i];
    const def = KINDS[k].unit?.attack ?? KINDS[k].building?.attack;
    const ah = p.hits[i] << 2;
    const t = w.resolve(p.target[i]);
    if (t >= 0) applyDamage(w, t, computeDamage(w, owner, k, t, ah), owner, false);
    if (def?.splash) splashDamage(w, owner, k, p.tx[i], p.ty[i], Math.round(def.splash * FX_ONE), t, ah);
  }
}

function splashDamage(w: World, owner: number, kindIdx: number, x: number, y: number, r: number, exclude: number, ah: number): void {
  const victims: number[] = [];
  w.forUnitsInRadius(x, y, r, (o) => {
    if (o !== exclude && w.areEnemies(owner, w.owner[o])) victims.push(o);
  });
  w.forStaticInRadius(x, y, r, (o) => {
    if (o !== exclude && w.cat[o] === Cat.Building && w.areEnemies(owner, w.owner[o]) && w.edgeDist(o, x, y) <= r) victims.push(o);
  });
  for (const o of victims) {
    if (!w.alive[o]) continue;
    let d = computeDamage(w, owner, kindIdx, o, ah) >> 1;
    if (w.cat[o] === Cat.Unit && w.formation[o] === 6) d = Math.trunc((d * 60) / 100); // loose formation
    applyDamage(w, o, Math.max(1, d), owner, true);
  }
}

/** Distance from attacker s to target edge. */
function attackDist(w: World, s: number, t: number): number {
  return w.edgeDist(t, w.x[s], w.y[s]) - w.extent(s);
}

/** Find the best enemy target near s. Prefers military units, then other units, then buildings. */
export function acquireTarget(w: World, s: number, radius: number): number {
  const owner = w.owner[s];
  let best = -1;
  let bestScore = Infinity;
  const fog = w.visibility[owner];
  const isBuilding = w.cat[s] === Cat.Building;
  w.forUnitsInRadius(w.x[s], w.y[s], radius, (o, d2) => {
    if (!w.areEnemies(owner, w.owner[o])) return;
    if (fog && fog[(w.y[o] >> 10) * w.size + (w.x[o] >> 10)] !== 2) return;
    const oc = KINDS[w.kind[o]].unit!.classes;
    const pri = oc & C.Military ? 0 : 1;
    const score = pri * 1e12 + d2;
    if (score < bestScore || (score === bestScore && o < best)) {
      bestScore = score;
      best = o;
    }
  });
  if (best >= 0 || isBuilding) return best;
  w.forStaticInRadius(w.x[s], w.y[s], radius, (o, d2) => {
    if (w.cat[o] !== Cat.Building || !w.areEnemies(owner, w.owner[o])) return;
    if (fog && fog[(w.y[o] >> 10) * w.size + (w.x[o] >> 10)] === 0) return;
    if (d2 < bestScore || (d2 === bestScore && o < best)) {
      bestScore = d2;
      best = o;
    }
  });
  return best;
}

export function updateCombat(w: World): void {
  const n = w.highSlot;
  for (let s = 0; s < n; s++) {
    if (!w.alive[s] || w.cat[s] === Cat.Resource || w.cat[s] === Cat.Held) continue;
    if (w.cooldown[s] > 0) w.cooldown[s]--;
    const owner = w.owner[s];
    if (owner < 0) continue;
    const k = KINDS[w.kind[s]];
    const atk = k.unit?.attack ?? k.building?.attack;
    if (!atk) continue;
    const st = w.players[owner].stats[w.kind[s]];

    if (w.cat[s] === Cat.Building) {
      if (!w.built[s]) continue;
      let t = w.resolve(w.target[s]);
      if (t < 0 || (w.tick + s) % 10 === 0) {
        t = acquireTarget(w, s, st.range + w.extent(s));
        w.target[s] = t >= 0 ? w.idOf(t) : -1;
      }
      if (t >= 0 && w.cooldown[s] <= 0 && attackDist(w, s, t) <= st.range) {
        // Villagers sheltering inside shoot too: the volley doubles (two arrows at the least).
        const arrows = w.cargo[s] ? Math.max(2, st.arrows * 2) : st.arrows;
        for (let i = 0; i < arrows; i++) spawnProjectile(w, s, t, st.accuracy);
        w.cooldown[s] = st.cooldown;
        w.events.push({ e: 'attack', id: w.idOf(s), kind: w.kind[s], owner, x: w.x[s], y: w.y[s], target: w.target[s] });
      }
      continue;
    }

    const isVillager = (k.unit!.classes & C.Villager) !== 0;
    // A newly trained soldier attacked on the way to its rally point turns on the nearest enemy at
    // once, then carries on to the rally point (as an attack-move).
    if (w.rallying[s] && w.order[s] === Order.Move && w.tick - w.lastHurtTick[s] <= 1 && w.stance[s] !== StanceId.Passive) {
      const t = acquireTarget(w, s, Math.max(st.range, st.los * FX_ONE));
      if (t >= 0) {
        w.order[s] = Order.AttackMove;
        w.target[s] = w.idOf(t);
        w.timer[s] = 0;
      }
    }
    const ord = w.order[s];
    // Auto-acquire for idle / attack-moving / guarding military units.
    if (!isVillager && (ord === Order.None || ord === Order.AttackMove || ord === Order.Patrol || ord === Order.Guard) && (w.tick + s) % 6 === 0) {
      const stance = w.stance[s];
      if (stance !== StanceId.Passive) {
        const r = stance === StanceId.Hold ? st.range + w.extent(s) + FX_ONE / 2 : Math.max(st.range, st.los * FX_ONE);
        const t = acquireTarget(w, s, r);
        if (t >= 0) {
          w.order[s] = ord === Order.None ? Order.Attack : ord;
          w.target[s] = w.idOf(t);
          if (ord === Order.None) {
            w.homeX[s] = w.x[s];
            w.homeY[s] = w.y[s];
          }
          w.timer[s] = 0;
        }
      }
    }

    const t = w.resolve(w.target[s]);
    if (ord !== Order.Attack && ord !== Order.AttackMove && ord !== Order.Patrol && ord !== Order.Guard) continue;
    if (t < 0 || !w.areEnemies(owner, w.owner[t]) || w.cat[t] === Cat.Held) {
      w.target[s] = -1;
      if (ord === Order.Attack) {
        w.order[s] = Order.None;
        w.state[s] = UState.Idle;
        stopMoving(w, s);
      } else if ((ord === Order.AttackMove || ord === Order.Patrol) && w.state[s] === UState.Attacking) {
        w.state[s] = UState.Walking;
        setDestination(w, s, w.goalX[s], w.goalY[s]);
      }
      continue;
    }
    // Defensive stance: don't chase far from home.
    if (w.stance[s] === StanceId.Defensive && ord === Order.Attack) {
      const hx = w.homeX[s] - w.x[s];
      const hy = w.homeY[s] - w.y[s];
      if (hx * hx + hy * hy > 8 * FX_ONE * (8 * FX_ONE)) {
        w.target[s] = -1;
        w.order[s] = Order.Move;
        w.state[s] = UState.Walking;
        setDestination(w, s, w.homeX[s], w.homeY[s]);
        continue;
      }
    }
    const d = attackDist(w, s, t);
    const reach = atk.range > 0 ? st.range : FX_ONE / 4;
    if (d <= reach && d >= st.minRange - FX_ONE / 2) {
      if (w.moving[s]) stopMoving(w, s);
      w.state[s] = UState.Attacking;
      const dx = w.x[t] - w.x[s];
      const dy = w.y[t] - w.y[s];
      const dd = isqrt(dx * dx + dy * dy) || 1;
      w.fdx[s] = Math.trunc((dx * 1000) / dd);
      w.fdy[s] = Math.trunc((dy * 1000) / dd);
      if (w.cooldown[s] <= 0) {
        w.cooldown[s] = st.cooldown;
        w.lastAttackTick[s] = w.tick;
        w.events.push({ e: 'attack', id: w.idOf(s), kind: w.kind[s], owner, x: w.x[s], y: w.y[s], target: w.target[s] });
        if (atk.range > 0 && atk.projectile) {
          spawnProjectile(w, s, t, st.accuracy);
        } else {
          let dmg = computeDamage(w, owner, w.kind[s], t, w.heightAt(w.x[s], w.y[s]));
          if (w.formation[s] === 4 && k.unit!.classes & C.Cavalry) dmg = Math.trunc((dmg * 120) / 100);
          applyDamage(w, t, dmg, owner, false);
          if (atk.splash) splashDamage(w, owner, w.kind[s], w.x[t], w.y[t], Math.round(atk.splash * FX_ONE), t, w.heightAt(w.x[s], w.y[s]));
        }
      }
    } else if (w.stance[s] === StanceId.Hold) {
      w.target[s] = -1;
    } else {
      // Chase: repath periodically as the target moves.
      w.state[s] = UState.Walking;
      if (!w.moving[s] || w.timer[s] <= 0) {
        let gx = w.x[t];
        let gy = w.y[t];
        if (d < st.minRange) {
          // Too close for siege: back away.
          gx = w.x[s] * 2 - w.x[t];
          gy = w.y[s] * 2 - w.y[t];
        }
        if (d >= st.minRange && w.cat[t] === Cat.Building) approach(w, s, t);
        else setDestination(w, s, gx, gy);
        w.timer[s] = 12;
      } else w.timer[s]--;
    }
  }
  updateProjectiles(w);
}
